import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { fetchProfile, patchProfile } from '../api/profileApi';
import { AssetRequestError } from '../api/getAssetById';
import { getStoredLoginIdentifier, getSessionToken } from '../auth/sessionToken';
import { useAuth } from '../auth/AuthContext';
import { useI18n } from '../i18n';
import {
  extractProfileDisplaySummary,
  type ProfileDisplaySummary,
} from '../utils/profileDisplaySummary';
import {
  normalizeProfileVehiclesPrimary,
  profileVehiclesToWire,
  validateProfileVehicles,
  type ProfileVehicle,
} from '../utils/profileVehicles';
import { clearStoredProfileVehicles } from '../utils/profileVehiclesStorage';
import { assertProfileMutationSuccess } from '../utils/profileWorkerList';
import { ProfileVehiclesEditSection } from './ProfileVehiclesEditSection';
import pageThemeStyles from './menuPageTheme.module.css';
import styles from './ProfileMenu.module.css';

export type ProfileVehiclesEditPanelProps = {
  onBack: () => void;
  onSaved?: (summary: ProfileDisplaySummary) => void;
  /** Optional seed while profile loads. */
  initialVehicles?: ProfileVehicle[];
};

export function ProfileVehiclesEditPanel({
  onBack,
  onSaved,
  initialVehicles = [],
}: ProfileVehiclesEditPanelProps) {
  const { t, language } = useI18n();
  const { userName } = useAuth();
  const panelId = useId();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<ProfileVehicle[]>(initialVehicles);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProfile();
        if (cancelled) {
          return;
        }
        const summary = extractProfileDisplaySummary(
          data,
          userName,
          getStoredLoginIdentifier(),
          getSessionToken(),
        );
        setVehicles(summary.vehicles ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof AssetRequestError
              ? err.message
              : err instanceof Error
                ? err.message
                : t('common.somethingWrong'),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t, userName]);

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const normalized = normalizeProfileVehiclesPrimary(vehicles);
      const issue = validateProfileVehicles(normalized);
      if (issue) {
        const messageKey =
          issue === 'max'
            ? 'profile.vehicles.errorMax'
            : issue === 'license'
              ? 'profile.vehicles.errorLicense'
              : issue === 'duplicateLicense'
                ? 'profile.vehicles.errorDuplicateLicense'
                : issue === 'primary'
                  ? 'profile.vehicles.errorPrimary'
                  : 'profile.vehicles.errorIncomplete';
        setError(t(messageKey));
        return;
      }
      setSaving(true);
      setError(null);
      try {
        const response = await patchProfile({ vehicles: profileVehiclesToWire(normalized) });
        assertProfileMutationSuccess(response, t('profile.operation.patchProfile'));
        clearStoredProfileVehicles(getStoredLoginIdentifier());
        const data = await fetchProfile();
        const summary = extractProfileDisplaySummary(
          data,
          userName,
          getStoredLoginIdentifier(),
          getSessionToken(),
        );
        onSaved?.({ ...summary, vehicles: normalized });
      } catch (err) {
        setError(
          err instanceof AssetRequestError
            ? err.message
            : err instanceof Error
              ? err.message
              : t('common.somethingWrong'),
        );
      } finally {
        setSaving(false);
      }
    },
    [onSaved, t, userName, vehicles],
  );

  const textDir = language === 'he' ? 'rtl' : 'ltr';

  return (
    <div
      className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed].join(' ')}
      role="region"
      aria-label={t('profile.hub.editVehicles')}
    >
      <div className={[pageThemeStyles.panelEmbedded, pageThemeStyles.browsePage].join(' ')}>
        <div className={styles.hubSubPanel} dir={textDir}>
          <div className={styles.hubTopBar}>
            <button
              type="button"
              className={styles.hubTopBarBtn}
              onClick={onBack}
              aria-label={t('profile.hub.back')}
            >
              <ProfileBackIcon rtl={language === 'he'} />
            </button>
            <h2 id={panelId} className={styles.hubTopBarTitle}>
              {t('profile.hub.editVehicles')}
            </h2>
            <span className={styles.hubTopBarSpacer} aria-hidden />
          </div>

          {loading ? (
            <p className={pageThemeStyles.browseEmpty} role="status">
              {t('common.loading')}
            </p>
          ) : (
            <form className={styles.profileEditForm} dir={textDir} onSubmit={onSubmit}>
              <ProfileVehiclesEditSection
                vehicles={vehicles}
                onChange={setVehicles}
                disabled={saving}
              />
              {error ? (
                <p className={styles.profileEditError} role="alert">
                  {error}
                </p>
              ) : null}
              <button type="submit" className={styles.profileEditSaveBtn} disabled={saving}>
                {saving ? t('common.submitting') : t('profile.edit.save')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileBackIcon({ rtl }: { rtl: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path
        d={rtl ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
